/**
 * Sistema de gamificación para modo infantil
 */

export type BadgeRarity = 'common' | 'rare' | 'epic' | 'legendary' | 'mythic';

export type BadgeRequirementType = 'streak' | 'count' | 'category' | 'first_time' | 'special';

export interface BadgeRequirement {
  type: BadgeRequirementType;
  value?: number;
  category?: string;
}

export interface Badge {
  id: string;
  emoji: string;
  title: string;
  description: string;
  requirement: BadgeRequirement;
  rarity: BadgeRarity;
  points?: number; // Puntos extra al desbloquear
}

export interface UserBadge {
  badgeId: string;
  unlockedAt: Date;
  memberId?: string; // Para vincular con miembro específico
}

export interface DailyStreak {
  memberId: string;
  currentStreak: number;
  longestStreak: number;
  lastActivityDate: Date;
}

export type ChildMode = 'child' | 'tween' | 'teen' | 'adult';

export interface ChildThemeConfig {
  mode: ChildMode;
  animations: boolean;
  sounds: boolean;
  celebrationsEnabled: boolean;
  simplifiedUI: boolean;
  fontSize: 'small' | 'medium' | 'large' | 'xlarge';
}

export interface MemberDisplayConfig {
  memberId: string;
  childMode: ChildMode;
  themeConfig: ChildThemeConfig;
  badges: UserBadge[];
  streak: DailyStreak;
}

// Biblioteca de badges predefinidos
export const BADGE_LIBRARY: Badge[] = [
  // Primeros pasos
  {
    id: 'first_activity',
    emoji: '🌟',
    title: '¡Primera vez!',
    description: 'Completaste tu primera actividad',
    requirement: { type: 'first_time' },
    rarity: 'common',
    points: 10
  },
  {
    id: 'week_complete',
    emoji: '🎯',
    title: 'Semana completa',
    description: 'Completaste todas las actividades de la semana',
    requirement: { type: 'special' },
    rarity: 'rare',
    points: 50
  },
  // Rachas
  {
    id: 'chispa_inicial',
    emoji: '✨',
    title: 'Chispa',
    description: '3 días de racha',
    requirement: { type: 'streak', value: 3 },
    rarity: 'common',
    points: 25
  },
  {
    id: 'semana_perfecta',
    emoji: '⚡',
    title: 'Semana',
    description: '7 días de racha',
    requirement: { type: 'streak', value: 7 },
    rarity: 'rare',
    points: 50
  },
  {
    id: 'ritmo_imparable',
    emoji: '🚀',
    title: 'Imparable',
    description: '14 días de racha',
    requirement: { type: 'streak', value: 14 },
    rarity: 'rare',
    points: 100
  },
  {
    id: 'en_llamas',
    emoji: '🔥',
    title: 'En Llamas',
    description: '30 días de racha',
    requirement: { type: 'streak', value: 30 },
    rarity: 'epic',
    points: 250
  },
  {
    id: 'rutina_acero',
    emoji: '⚙️',
    title: 'Acero',
    description: '60 días de racha',
    requirement: { type: 'streak', value: 60 },
    rarity: 'epic',
    points: 400
  },
  {
    id: 'club_centenario',
    emoji: '👑',
    title: 'Centenario',
    description: '100 días de racha',
    requirement: { type: 'streak', value: 100 },
    rarity: 'legendary',
    points: 750
  },
  {
    id: 'maestro_habito',
    emoji: '🧙',
    title: 'Maestro',
    description: '180 días de racha',
    requirement: { type: 'streak', value: 180 },
    rarity: 'legendary',
    points: 1200
  },
  {
    id: 'leyenda_viviente',
    emoji: '🏆',
    title: 'Leyenda',
    description: '365 días de racha',
    requirement: { type: 'streak', value: 365 },
    rarity: 'mythic',
    points: 3000
  },
  {
    id: 'guardian_constancia',
    emoji: '🦁',
    title: 'Guardián',
    description: 'Consistencia de 100 días adicionales',
    requirement: { type: 'streak', value: 465 },
    rarity: 'mythic',
    points: 1000
  },
  // Contadores
  {
    id: 'activities_10',
    emoji: '⭐',
    title: 'Buen comienzo',
    description: 'Completaste 10 actividades',
    requirement: { type: 'count', value: 10 },
    rarity: 'common',
    points: 20
  },
  {
    id: 'activities_50',
    emoji: '⭐⭐',
    title: 'Comprometido',
    description: 'Completaste 50 actividades',
    requirement: { type: 'count', value: 50 },
    rarity: 'rare',
    points: 50
  },
  {
    id: 'activities_100',
    emoji: '⭐⭐⭐',
    title: 'Experto',
    description: 'Completaste 100 actividades',
    requirement: { type: 'count', value: 100 },
    rarity: 'epic',
    points: 100
  },
  {
    id: 'activities_500',
    emoji: '💎',
    title: 'Diamante',
    description: 'Completaste 500 actividades',
    requirement: { type: 'count', value: 500 },
    rarity: 'legendary',
    points: 300
  },
  // Categorías
  {
    id: 'hygiene_master',
    emoji: '🧼',
    title: 'Limpio y reluciente',
    description: 'Completaste 20 actividades de higiene',
    requirement: { type: 'category', value: 20, category: 'hygiene' },
    rarity: 'rare',
    points: 40
  },
  {
    id: 'homework_hero',
    emoji: '📚',
    title: 'Estudiante estrella',
    description: 'Completaste 20 tareas escolares',
    requirement: { type: 'category', value: 20, category: 'homework' },
    rarity: 'rare',
    points: 40
  },
  {
    id: 'chores_champion',
    emoji: '🏆',
    title: 'Campeón del hogar',
    description: 'Completaste 20 tareas domésticas',
    requirement: { type: 'category', value: 20, category: 'chores' },
    rarity: 'rare',
    points: 40
  },
  // Especiales
  {
    id: 'early_bird',
    emoji: '🌅',
    title: 'Madrugador',
    description: 'Completaste actividades antes de las 8am durante 5 días',
    requirement: { type: 'special' },
    rarity: 'epic',
    points: 60
  },
  {
    id: 'helper',
    emoji: '🤝',
    title: 'Ayudante familiar',
    description: 'Ayudaste a un hermano con sus tareas',
    requirement: { type: 'special' },
    rarity: 'rare',
    points: 30
  }
];

// Configuraciones predefinidas por edad
export const AGE_CONFIGS: Record<ChildMode, ChildThemeConfig> = {
  child: {
    mode: 'child',
    animations: true,
    sounds: false,
    celebrationsEnabled: true,
    simplifiedUI: true,
    fontSize: 'xlarge'
  },
  tween: {
    mode: 'tween',
    animations: true,
    sounds: false,
    celebrationsEnabled: true,
    simplifiedUI: false,
    fontSize: 'large'
  },
  teen: {
    mode: 'teen',
    animations: false,
    sounds: false,
    celebrationsEnabled: false,
    simplifiedUI: false,
    fontSize: 'medium'
  },
  adult: {
    mode: 'adult',
    animations: false,
    sounds: false,
    celebrationsEnabled: false,
    simplifiedUI: false,
    fontSize: 'medium'
  }
};

// Helper para obtener color por rareza
export function getRarityColor(rarity: BadgeRarity): string {
  switch (rarity) {
    case 'common':
      return 'text-gray-500 dark:text-gray-400';
    case 'rare':
      return 'text-blue-500 dark:text-blue-400';
    case 'epic':
      return 'text-purple-500 dark:text-purple-400';
    case 'legendary':
      return 'text-amber-500 dark:text-amber-400';
    case 'mythic':
      return 'text-rose-500 dark:text-rose-400';
  }
}

// Helper para obtener color de fondo por rareza
export function getRarityBgColor(rarity: BadgeRarity): string {
  switch (rarity) {
    case 'common':
      return 'bg-gray-100 dark:bg-gray-800';
    case 'rare':
      return 'bg-blue-100 dark:bg-blue-900/30';
    case 'epic':
      return 'bg-purple-100 dark:bg-purple-900/30';
    case 'legendary':
      return 'bg-amber-100 dark:bg-amber-900/30';
    case 'mythic':
      return 'bg-rose-100 dark:bg-rose-900/30';
  }
}
